import { ApiError, createApiClient, Result, wrapper } from "@/api/base";

export interface NominatimResult {
	place_id: number;
	osm_type: "node" | "way" | "relation";
	osm_id: number;
	display_name: string;
	type: string;
	class: string;
	lat: string;
	lon: string;
}
import { LaravelPaginatedCollection } from "@/types/collections";
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;
import ProductPoolDO = Modules.CampaignPlanner.Data.ProductPoolDO;
import OpportunityDO = Modules.CampaignPlanner.Data.OpportunityDO;
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import FlightSummaryDTO = Modules.CampaignPlanner.Data.FlightSummaryDTO;
import FlightSegmentSummaryDO = Modules.CampaignPlanner.Data.FlightSegmentSummaryDO;
import SaveSegmentSelectionsDO = Modules.CampaignPlanner.Data.SaveSegmentSelectionsDO;
import CriterionDO = Modules.CampaignPlanner.Data.CriterionDO;
import AvailableScreensDO = Modules.CampaignPlanner.Data.AvailableScreensDO;

const basePath = "campaign-planner";
const baseClient = createApiClient();

export default class {
	getScreens(params: {
		page: number;
		per_page?: number;
		sort?: string;
		"filter[query]"?: string;
		"filter[active]"?: string;
		"filter[is_hidden]"?: string;
		"filter[has_reference]"?: string;
		"filter[inventory_type]"?: string;
	}): Promise<Result<LaravelPaginatedCollection<ScreenDO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/screens`, { params }));
	}

	updateScreen(
		id: number,
		data: Pick<
			ScreenDO,
			"is_hidden" | "is_mandatory_must" | "minimum_advertising_cost_ex_vat"
		>
	): Promise<Result<ScreenDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/screens/${id}`, data));
	}

	getSizes(params: {
		page: number;
		per_page?: number;
		sort?: string;
		"filter[label]"?: string;
		"filter[orientation]"?: string;
		"filter[inventory_type]"?: string;
		"filter[location]"?: string;
	}): Promise<Result<LaravelPaginatedCollection<SizeDO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/sizes`, { params }));
	}

	createSize(data: {
		label: string;
		orientation: string;
		inventory_type: string;
		location: string;
		width?: number | null;
		height?: number | null;
		screen_ids: number[];
	}): Promise<Result<SizeDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/sizes`, data));
	}

	updateSize(
		id: number,
		data: {
			label: string;
			orientation: string;
			inventory_type: string;
			location: string;
			width?: number | null;
			height?: number | null;
			screen_ids: number[];
		}
	): Promise<Result<SizeDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/sizes/${id}`, data));
	}

	deleteSize(id: number): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/sizes/${id}`));
	}

	getProductPools(params: {
		page: number;
		per_page?: number;
		sort?: string;
		"filter[identifier]"?: string;
		"filter[is_single_location_for_spread_algorithm]"?: string;
	}): Promise<Result<LaravelPaginatedCollection<ProductPoolDO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/product-pools`, { params }));
	}

	createProductPool(data: {
		identifier: string;
		is_single_location_for_spread_algorithm: boolean;
		pqa_allowed_amounts_screens: number[];
		screen_ids: number[];
	}): Promise<Result<ProductPoolDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/product-pools`, data));
	}

	updateProductPool(
		uuid: string,
		data: {
			identifier: string;
			is_single_location_for_spread_algorithm: boolean;
			pqa_allowed_amounts_screens: number[];
			screen_ids: number[];
		}
	): Promise<Result<ProductPoolDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/product-pools/${uuid}`, data));
	}

	deleteProductPool(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/product-pools/${uuid}`));
	}

	getOpportunity(uuid: string): Promise<Result<OpportunityDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/opportunities/${uuid}`));
	}

	getFlight(uuid: string): Promise<Result<FlightDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/flights/${uuid}`));
	}

	getFlightCriteria(): Promise<Result<CriterionDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/criteria`));
	}

	getFlightAvailableScreens(
		flight: FlightDO,
		signal?: AbortSignal
	): Promise<Result<AvailableScreensDO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/screens/available-for-flight`, flight, {
				signal,
			})
		);
	}

	getOpportunityFlights(
		uuid: string
	): Promise<Result<FlightSummaryDTO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/opportunities/${uuid}/flights`));
	}

	createFlight(
		opportunityUuid: string,
		name: string
	): Promise<Result<FlightSummaryDTO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/opportunities/${opportunityUuid}/flights`, {
				name,
			})
		);
	}

	getFlightSegments(
		flightUuid: string
	): Promise<Result<FlightSegmentSummaryDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/flights/${flightUuid}/segments`)
		);
	}

	updateFlight(
		uuid: string,
		data: { name: string }
	): Promise<Result<FlightSummaryDTO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/flights/${uuid}`, data));
	}

	deleteFlight(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/flights/${uuid}`));
	}

	saveSegmentSelections(
		flightUuid: string,
		data: SaveSegmentSelectionsDO
	): Promise<Result<null, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/flights/${flightUuid}/segments/selections`,
				data
			)
		);
	}

	importScreenSelectionFromCsv(
		flight: FlightDO,
		file: File
	): Promise<Result<ScreenDO[], ApiError>> {
		const form = new FormData();
		form.append("file", file);
		form.append("flight", JSON.stringify(flight));
		return wrapper(
			baseClient.post(`${basePath}/screens/import-screen-selection`, form, {
				headers: { "Content-Type": "multipart/form-data" },
			})
		);
	}

	updateFlightPricingOverwrite(
		uuid: string,
		data: { cost_overwrite: number | null; cpm_overwrite: number | null }
	): Promise<Result<FlightSummaryDTO, ApiError>> {
		return wrapper(
			baseClient.patch(`${basePath}/flights/${uuid}/pricing-overwrite`, data)
		);
	}

	retryFlightSegment(
		flightUuid: string,
		segmentUuid: string
	): Promise<Result<null, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/flights/${flightUuid}/segments/${segmentUuid}/retry`
			)
		);
	}

	cloneFlight(flight: FlightDO): Promise<Result<FlightDO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/flights/${flight.uuid}/clone`, flight)
		);
	}

	saveFlight(
		flight: FlightDO,
		useInternalPqaEngine = false
	): Promise<Result<FlightDO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/flights/${flight.uuid}/save`, {
				...flight,
				use_internal_pqa_engine: useInternalPqaEngine,
			})
		);
	}

	syncOpportunityToBroadsign(uuid: string): Promise<Result<null, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/opportunities/${uuid}/sync-to-broadsign`));
	}

	searchLocations(q: string): Promise<Result<NominatimResult[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/locations/search`, { params: { q } })
		);
	}

	getOpportunities(params: {
		page: number;
		per_page?: number;
		sort?: string;
		"filter[query]"?: string;
		"filter[campaign_type]"?: string;
		"filter[sales_source]"?: string;
		"filter[synchronized]"?: string;
	}): Promise<Result<LaravelPaginatedCollection<OpportunityDO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/opportunities`, { params }));
	}
}
